/**
 * Simplified Brand Icons System
 * 
 * Based on the ui-main reference implementation - clean, simple SVG definitions
 * with no loading states, complex abstractions, or external dependencies.
 */

import React from 'react';
import { Icon } from '@iconify/react';

interface BrandIconProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
}

export const BrandIcons = {
  // AI Assistants
  anthropic: (props: BrandIconProps) => (
    <Icon 
      icon="simple-icons:anthropic" 
      width={props.width || 24} 
      height={props.height || 24} 
      className={props.className}
    />
  ),
  openai: (props: BrandIconProps) => (
    <Icon 
      icon="simple-icons:openai" 
      width={props.width || 24} 
      height={props.height || 24} 
      className={props.className}
    />
  ),

  // Automation
  n8n: (props: BrandIconProps) => (
    <Icon 
      icon="simple-icons:n8n" 
      width={props.width || 24} 
      height={props.height || 24} 
      className={props.className}
    />
  ),
  dify: (props: BrandIconProps) => (
    <Icon
      icon="simple-icons:dify"
      width={props.width || 24}
      height={props.height || 24}
      className={props.className}
    />
  ),

  // Communication
  slack: (props: BrandIconProps) => (
    <Icon 
      icon="simple-icons:slack" 
      width={props.width || 24} 
      height={props.height || 24} 
      className={props.className}
    />
  ),
  discord: (props: BrandIconProps) => (
    <Icon 
      icon="simple-icons:discord" 
      width={props.width || 24} 
      height={props.height || 24} 
      className={props.className}
    />
  ),
  telegram: (props: BrandIconProps) => (
    <Icon 
      icon="simple-icons:telegram" 
      width={props.width || 24} 
      height={props.height || 24} 
      className={props.className}
    />
  ),
  microsoftteams: (props: BrandIconProps) => (
    <Icon 
      icon="simple-icons:microsoftteams" 
      width={props.width || 24} 
      height={props.height || 24} 
      className={props.className}
    />
  ),
  whatsapp: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"/>
    </svg>
  ),

  // Automation
  zapier: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M24 12.473v5.058a4.469 4.469 0 0 1-4.469 4.469h-6.637V11.883h11.106v.59zM7.447 2.682h9.105a4.469 4.469 0 0 1 4.469 4.469v4.732H10.915v11.117H4.469A4.469 4.469 0 0 1 0 18.531V7.151a4.469 4.469 0 0 1 4.469-4.469h2.978v-.007z"/>
    </svg>
  ),
  make: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M8.9 0C4.9 0 1.67 2.35 0 6.9c0 0 3.2-.6 4.4 0c1.2.6 1.2 1.2 2.4 1.2s1.2-.6 2.4-1.2c1.2-.6 4.4 0 4.4 0C12.3 2.35 9.07 0 8.9 0zM24 6.9c-1.67-4.55-4.9-6.9-8.9-6.9-.17 0-.4.03-.4.03s3.2 2.03 4.4 6.03c0 0 3.2-.6 4.4 0c1.2.6 1.2 1.2 2.4 1.2s1.2-.6 2.4-1.2C24.4 6.3 24 6.9 24 6.9z"/>
    </svg>
  ),
  ifttt: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M0 7.333h3.111V24H0zm20.889 0H24V24h-3.111zM7.333 0v3.111H24V0zm0 7.333v3.111h16.556V7.333zm0 7.334v3.111h16.556v-3.111z"/>
    </svg>
  ),

  // Productivity
  notion: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233c.747 0 .934.092 1.401.653l4.944 7.92v-6.112l-1.07-.093c-.094-.513.28-.887.793-.934z"/>
    </svg>
  ),
  google: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),

  // Development
  github: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  ),
  cursor: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2L2 7v10c0 5.55 3.84 9.739 9 11 5.16-1.261 9-5.45 9-11V7l-10-5z"/>
      <path d="M8 12l2 2 6-6" stroke="currentColor" strokeWidth="2" fill="none"/>
    </svg>
  ),

  // Browser
  googlechrome: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.017 1.33c3.05 0 5.88 1.315 7.857 3.609l-3.308 1.908c-.706-.6-1.611-.954-2.566-.954-1.72 0-3.198 1.103-3.718 2.639l-3.308-1.908C8.137 3.315 10.967 2 14.017 2zM6.623 5.396l3.308 1.908c-.273.545-.423 1.157-.423 1.803 0 2.21 1.79 4 4 4s4-1.79 4-4c0-.646-.15-1.258-.423-1.803l3.308-1.908C21.395 6.623 22 9.217 22 12c0 5.523-4.477 10-10 10S2 17.523 2 12c0-2.783.604-5.377 1.623-7.604z"/>
    </svg>
  ),

  // Mobile
  ios: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
    </svg>
  ),

  // Social Media
  twitter: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
    </svg>
  ),

  // Generic/API
  api: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2L2 7v10c0 5.55 3.84 9.739 9 11 5.16-1.261 9-5.45 9-11V7l-10-5z"/>
      <path d="M8 12h8M8 15h8M8 9h8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    </svg>
  ),
  protocol: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2L2 7v10c0 5.55 3.84 9.739 9 11 5.16-1.261 9-5.45 9-11V7l-10-5z"/>
      <path d="M8 12h8M8 15h8M8 9h8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    </svg>
  ),
  mcp: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2L2 7v10c0 5.55 3.84 9.739 9 11 5.16-1.261 9-5.45 9-11V7l-10-5z"/>
      <path d="M9 10h6v4H9z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      <path d="M10.5 12h3M10.5 13h3" stroke="currentColor" strokeWidth="1" fill="none"/>
    </svg>
  ),
  fastapi: (props: BrandIconProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2L2 7v10c0 5.55 3.84 9.739 9 11 5.16-1.261 9-5.45 9-11V7l-10-5z"/>
      <path d="M8 12h8M8 15h8M8 9h8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    </svg>
  ),
} as const;

export type BrandIconName = keyof typeof BrandIcons;

/**
 * Simplified Integration Icon Component
 * 
 * No loading states, no complex abstractions - just clean, reliable icon display
 */
interface IntegrationIconProps {
  iconName: BrandIconName;
  className?: string;
  size?: number;
  fallback?: string;
}

export const IntegrationIcon: React.FC<IntegrationIconProps> = ({
  iconName,
  className = "w-6 h-6",
  size = 24,
  fallback
}) => {
  const IconComponent = BrandIcons[iconName];
  
  if (IconComponent) {
    return (
      <IconComponent
        className={className}
        width={size}
        height={size}
        style={{ color: 'currentColor' }}
      />
    );
  }
  
  // Clean fallback - always visible, no loading states
  return (
    <div 
      className={`${className} bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center text-gray-700 font-semibold`}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {fallback || iconName.slice(0, 2).toUpperCase()}
    </div>
  );
};

// Legacy compatibility - map old icon names to new ones
export const IconNameMap = {
  // Simple Icons format mapping
  'simple-icons:anthropic': 'anthropic' as BrandIconName,
  'simple-icons:openai': 'openai' as BrandIconName,
  'simple-icons:slack': 'slack' as BrandIconName,
  'simple-icons:discord': 'discord' as BrandIconName,
  'simple-icons:telegram': 'telegram' as BrandIconName,
  'simple-icons:microsoftteams': 'microsoftteams' as BrandIconName,
  'simple-icons:whatsapp': 'whatsapp' as BrandIconName,
  'simple-icons:notion': 'notion' as BrandIconName,
  'simple-icons:zapier': 'zapier' as BrandIconName,
  'simple-icons:make': 'make' as BrandIconName,
  'simple-icons:ifttt': 'ifttt' as BrandIconName,
  'simple-icons:google': 'google' as BrandIconName,
  'simple-icons:github': 'github' as BrandIconName,
  'cursor': 'cursor' as BrandIconName,
  'simple-icons:googlechrome': 'googlechrome' as BrandIconName,
  'simple-icons:ios': 'ios' as BrandIconName,
  'simple-icons:twitter': 'twitter' as BrandIconName,
  'api': 'api' as BrandIconName,
  'mcp': 'mcp' as BrandIconName,
} as const; 